// Date formatting for the staff read view of /athletes/:userId.
//
// Server datetimes are naive UTC ("2026-10-24T13:12:00", no suffix): parse them
// as UTC and render local, the same rule as fmtDateTime in
// AdminAthleteProfilePage.js. Date-only strings ("2027-05-01") are calendar
// dates and must NOT shift a day west of UTC, so they parse as local midnight.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function parseServerDate(value) {
  if (!value) return null;
  const s = String(value);
  let d;
  if (DATE_ONLY.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  }
  return isNaN(d.getTime()) ? null : d;
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayDiff = (d, now) => Math.round((startOfDay(now) - startOfDay(d)) / 86400000);

/** "Mar 14, 2025" */
export function fmtDate(value) {
  const d = parseServerDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

/** "May 2027" */
export function fmtMonthYear(value) {
  const d = parseServerDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
}

/** "Today, 9:12 AM" / "Yesterday, 4:03 PM" / "Oct 2, 9:12 AM" / "Oct 2, 2024, 9:12 AM" */
export function fmtDateTime(value, now = new Date()) {
  const d = parseServerDate(value);
  if (!d) return '';
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const diff = dayDiff(d, now);
  if (diff === 0) return `Today, ${time}`;
  if (diff === 1) return `Yesterday, ${time}`;
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return `${d.toLocaleDateString('en-US', opts)}, ${time}`;
}

/** "today" / "yesterday" / "5 days ago" / "Sep 2, 2026" (past a week) */
export function fmtLastSeen(value, now = new Date()) {
  const d = parseServerDate(value);
  if (!d) return '';
  const diff = dayDiff(d, now);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7) return `${diff} days ago`;
  return fmtDate(value);
}

/** "19 months ago" / "3 years ago" / "this month" — for "Signed up". */
export function fmtAgo(value, now = new Date()) {
  const d = parseServerDate(value);
  if (!d) return '';
  const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
    - (now.getDate() < d.getDate() ? 1 : 0);
  if (months <= 0) {
    const days = dayDiff(d, now);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
  }
  if (months < 24) return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  return `${Math.floor(months / 12)} years ago`;
}
